import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const SNAPP_BLUE = '#008EFA';

export interface EditorItem {
  _id: string;
  productTitle: string;
  quantity: number;
  refundedQuantity?: number;
  originalUnitPrice?: number;
  unitPrice: number;
  discountAmount?: number;
  totalPrice: number;
  status: string;
  accountsDelivered?: number;
  fulfillmentType?: string;
  postalDeliveryDetails?: { shippingCost?: number };
  selectedCapacity?: { name?: string; platformLabel?: string };
  /** واحدهای یک خرید چندتایی با همین کلید به هم وصل‌اند */
  groupKey?: string;
  parentOrderItemId?: string;
  unitNumber?: number;
  unitTotalInGroup?: number;
}

export interface EditorChange {
  orderItemId: string;
  newQuantity: number;
}

interface Props {
  items: EditorItem[];
  /** عدد فارسی‌شده با جداکنندهٔ هزار */
  format: (n: number) => string;
  busy?: boolean;
  /** زمان آخرین اکشن اسنپ‌پی روی این سفارش (paymentDetails.snapppayLastActionAt) */
  lastActionAt?: string | Date | null;
  onSubmit: (changes: EditorChange[], summary: { refund: number; willCancel: boolean; lines: string[] }) => void;
  onCancelOrder: () => void;
}

/** فاصلهٔ اجباری اسنپ‌پی بین دو تغییر روی یک سفارش — باید با MIN_ACTION_GAP_MS بک‌اند یکی باشد. */
const COOLDOWN_MS = 30_000;
const faNum = (n: number) => String(n).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/**
 * شمارش معکوس زندهٔ کولداون. بدون این، ادمین بعد از «کاهش تعداد» بلافاصله «حذف» را می‌زند
 * و بک‌اند ۴۲۹ می‌دهد — خطایی که کاملاً قابل پیشگیری است.
 */
function useCooldown(lastActionAt?: string | Date | null): number {
  const startedAt = lastActionAt ? new Date(lastActionAt).getTime() : 0;
  const calc = () => (startedAt ? Math.max(0, COOLDOWN_MS - (Date.now() - startedAt)) : 0);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    if (!startedAt) return;
    const t = setInterval(() => {
      const v = startedAt ? Math.max(0, COOLDOWN_MS - (Date.now() - startedAt)) : 0;
      setLeft(v);
      if (v === 0) clearInterval(t);
    }, 500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAt]);
  return left;
}

type Unit = EditorItem & { activeQty: number; delivered: number };

interface Group {
  key: string;
  title: string;
  feature: string;
  unitGross: number;
  unitNet: number;
  unitShip: number;
  units: Unit[];
  activeQty: number;
  /** کف مجاز = واحدهای تحویل‌شده (قابل پس‌گرفتن نیستند) */
  minQty: number;
}

/**
 * ✏️ جدول ویرایش اقلام سفارش اسنپ‌پی — کاهش تعداد و حذف ردیف.
 *
 * 🔑 گروه‌بندی: در این سیستم هر واحد از یک خرید چندتایی یک سند جداگانه است
 * (`groupKey` / `parentOrderItemId`). بدون گروه‌بندی، خرید سه‌تایی سه ردیفِ
 * «تعداد ۱» نشان داده می‌شد. این‌جا واحدها در یک ردیف با تعداد واقعی جمع می‌شوند
 * و هنگام ثبت، کاهش تعداد به حذف همان تعداد واحد ترجمه می‌شود — دقیقاً همان
 * کاری که `buildCarts` سمت سرور برای سبد اسنپ‌پی می‌کند.
 *
 * پیش‌نویس است: چند ردیف را تغییر می‌دهید، جمع‌ها زنده حساب می‌شوند، و در پایان
 * یک بار ثبت ⇒ یک درخواست به اسنپ‌پی (نه چند تا، چون بین هر دو باید ۳۰ ثانیه فاصله باشد).
 */
const SnappPayOrderEditor: React.FC<Props> = ({ items, format, busy = false, lastActionAt, onSubmit, onCancelOrder }) => {
  const cooldownLeft = useCooldown(lastActionAt);
  const cooling = cooldownLeft > 0;
  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    for (const it of items) {
      if (['refunded', 'cancelled'].includes(String(it.status))) continue;
      const bought = Math.max(1, Number(it.quantity) || 1);
      const activeQty = Math.max(0, bought - (Number(it.refundedQuantity) || 0));
      if (activeQty <= 0) continue;

      const key = String(it.groupKey || it.parentOrderItemId || it._id);
      const unitGross = Math.round(Number(it.originalUnitPrice) || Number(it.unitPrice) || 0);
      const unitNet = Math.round(Number(it.unitPrice) || 0);
      const unitShip =
        it.fulfillmentType === 'postal_delivery' ? Math.round(Number(it.postalDeliveryDetails?.shippingCost) || 0) : 0;
      const delivered = Math.max(0, Number(it.accountsDelivered) || 0);
      const unit: Unit = { ...it, activeQty, delivered };

      const g = map.get(key);
      if (g) {
        g.units.push(unit);
        g.activeQty += activeQty;
        g.minQty += Math.min(delivered, activeQty);
      } else {
        map.set(key, {
          key,
          title: it.productTitle,
          feature: it.selectedCapacity?.name || it.selectedCapacity?.platformLabel || '—',
          unitGross,
          unitNet,
          unitShip,
          units: [unit],
          activeQty,
          minQty: Math.min(delivered, activeQty),
        });
      }
    }
    return Array.from(map.values());
  }, [items]);

  // پیش‌نویس: کلید گروه → تعداد جدید
  const [draft, setDraft] = useState<Record<string, number>>({});
  const qtyOf = (g: Group) => draft[g.key] ?? g.activeQty;
  const set = (g: Group, v: number) =>
    setDraft((d) => ({ ...d, [g.key]: Math.max(g.minQty, Math.min(g.activeQty, v)) }));
  const clear = (g: Group) => setDraft((d) => ({ ...d, [g.key]: g.minQty }));

  const calc = useMemo(() => {
    let goods = 0;
    let discount = 0;
    let shipping = 0;
    let refund = 0;
    let remaining = 0;
    const lines: string[] = [];
    for (const g of groups) {
      const q = qtyOf(g);
      remaining += q;
      goods += g.unitGross * q;
      discount += Math.max(0, g.unitGross - g.unitNet) * q;
      shipping += g.unitShip * q;
      if (q < g.activeQty) {
        refund += (g.unitNet + g.unitShip) * (g.activeQty - q);
        lines.push(q === 0 ? `«${g.title}» حذف می‌شود` : `«${g.title}» از ${g.activeQty} به ${q}`);
      }
    }
    return { goods, discount, shipping, refund, remaining, lines, payable: goods - discount + shipping };
  }, [groups, draft]);

  const dirty = calc.lines.length > 0;
  const willCancel = dirty && calc.remaining === 0;

  /**
   * ترجمهٔ «تعداد جدید گروه» به تغییر تک‌تک واحدها.
   * ابتدا واحدهای تحویل‌نشده و با شمارهٔ بالاتر حذف می‌شوند تا واحدهای تحویل‌شده دست‌نخورده بمانند.
   */
  const buildChanges = (): EditorChange[] => {
    const changes: EditorChange[] = [];
    for (const g of groups) {
      let toRemove = g.activeQty - qtyOf(g);
      if (toRemove <= 0) continue;
      const order = [...g.units].sort(
        (a, b) => a.delivered - b.delivered || (Number(b.unitNumber) || 0) - (Number(a.unitNumber) || 0),
      );
      for (const u of order) {
        if (toRemove <= 0) break;
        const removable = Math.max(0, u.activeQty - u.delivered);
        if (removable <= 0) continue;
        const take = Math.min(removable, toRemove);
        changes.push({ orderItemId: u._id, newQuantity: u.activeQty - take });
        toRemove -= take;
      }
    }
    return changes;
  };

  const submit = () => {
    const changes = buildChanges();
    if (!changes.length) return;
    onSubmit(changes, { refund: calc.refund, willCancel, lines: calc.lines });
  };

  if (!groups.length) {
    return (
      <div style={{ padding: 14, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 13 }}>
        قلم فعالی برای ویرایش باقی نمانده است.
      </div>
    );
  }

  const th: React.CSSProperties = {
    padding: '9px 10px',
    fontSize: 11.5,
    fontWeight: 700,
    color: 'var(--color-text-secondary)',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--color-border)',
  };
  const td: React.CSSProperties = {
    padding: '10px',
    fontSize: 13,
    borderBottom: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={th}>نام محصول</th>
              <th style={th}>ویژگی</th>
              <th style={th}>قیمت (تومان)</th>
              <th style={th}>تخفیف (تومان)</th>
              <th style={th}>قیمت با تخفیف (تومان)</th>
              <th style={th}>تعداد</th>
              <th style={th}>مبلغ کل (تومان)</th>
              <th style={{ ...th, textAlign: 'center' }}>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const q = qtyOf(g);
              const unitDiscount = Math.max(0, g.unitGross - g.unitNet);
              const removed = q === 0;
              return (
                <tr key={g.key} style={removed ? { opacity: 0.5 } : undefined}>
                  <td style={{ ...td, color: SNAPP_BLUE, fontWeight: 600, whiteSpace: 'normal', minWidth: 150 }}>
                    {g.title}
                    {g.units.length > 1 && (
                      <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400, fontSize: 11, marginRight: 6 }}>
                        ({format(g.units.length)} واحد)
                      </span>
                    )}
                    {removed && (
                      <span style={{ color: 'var(--color-error-dark)', fontWeight: 700, marginRight: 6, fontSize: 11 }}>
                        حذف می‌شود
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: 'var(--color-text-secondary)', fontSize: 12 }}>{g.feature}</td>
                  <td style={td}>{format(g.unitGross)}</td>
                  <td style={{ ...td, color: unitDiscount > 0 ? 'var(--color-error-dark)' : undefined }}>
                    {unitDiscount > 0 ? `−${format(unitDiscount)}` : format(0)}
                  </td>
                  <td style={td}>{format(g.unitNet)}</td>
                  <td style={{ ...td, color: q < g.activeQty ? 'var(--color-error-dark)' : undefined, fontWeight: 700 }}>
                    {q < g.activeQty ? `${format(g.activeQty)} ← ${format(q)}` : format(q)}
                  </td>
                  <td style={{ ...td, fontWeight: 700 }}>{format(g.unitNet * q)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <button
                        className="action-btn delete"
                        title="حذف کامل این ردیف"
                        disabled={busy || q <= g.minQty}
                        onClick={() => clear(g)}
                      >
                        <DeleteOutlineIcon />
                      </button>
                      <button
                        className="action-btn"
                        title="افزایش (تا سقف تعداد فعلی)"
                        disabled={busy || q >= g.activeQty}
                        onClick={() => set(g, q + 1)}
                      >
                        <AddIcon />
                      </button>
                      <input
                        type="number"
                        value={q}
                        min={g.minQty}
                        max={g.activeQty}
                        disabled={busy}
                        onChange={(e) => set(g, Number(e.target.value) || 0)}
                        style={{
                          width: 52,
                          padding: '5px 6px',
                          textAlign: 'center',
                          border: '1px solid var(--color-border)',
                          borderRadius: 7,
                          fontFamily: 'inherit',
                          fontSize: 13,
                          background: 'var(--color-bg-paper)',
                          color: 'var(--color-text-primary)',
                        }}
                      />
                      <button
                        className="action-btn"
                        title="کاهش"
                        disabled={busy || q <= g.minQty}
                        onClick={() => set(g, q - 1)}
                      >
                        <RemoveIcon />
                      </button>
                    </div>
                    {g.minQty > 0 && (
                      <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                        {format(g.minQty)} واحد تحویل شده
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* جمع‌ها — زنده با هر تغییر پیش‌نویس */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              <th style={th}>جمع کل (تومان)</th>
              <th style={th}>تخفیف (تومان)</th>
              <th style={th}>هزینه باربری (تومان)</th>
              <th style={th}>قابل پرداخت (تومان)</th>
              <th style={th}>بازگشت به خریدار (تومان)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...td, borderBottom: 'none' }}>{format(calc.goods)}</td>
              <td style={{ ...td, borderBottom: 'none', color: calc.discount > 0 ? 'var(--color-error-dark)' : undefined }}>
                {calc.discount > 0 ? `−${format(calc.discount)}` : format(0)}
              </td>
              <td style={{ ...td, borderBottom: 'none' }}>{format(calc.shipping)}</td>
              <td style={{ ...td, borderBottom: 'none', fontWeight: 800 }}>{format(calc.payable)}</td>
              <td
                style={{
                  ...td,
                  borderBottom: 'none',
                  fontWeight: 800,
                  color: calc.refund > 0 ? 'var(--color-error-dark)' : 'var(--color-text-secondary)',
                }}
              >
                {calc.refund > 0 ? format(calc.refund) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {willCancel && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 9,
            background: 'var(--color-error-bg)',
            color: 'var(--color-error-dark)',
            fontSize: 12.5,
            lineHeight: 1.9,
            fontWeight: 600,
          }}
        >
          همهٔ اقلام حذف شده‌اند؛ با ثبت این تغییرات، کل سفارش در اسنپ‌پی لغو می‌شود و تمام مبلغ به اعتبار خریدار برمی‌گردد.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="action-btn"
          disabled={busy || !dirty || cooling}
          onClick={submit}
          title={cooling ? 'اسنپ‌پی بین دو تغییر ۳۰ ثانیه فاصله می‌خواهد' : undefined}
          style={{ borderColor: SNAPP_BLUE, color: SNAPP_BLUE }}
        >
          {busy
            ? 'در حال ثبت…'
            : cooling
              ? `${faNum(Math.ceil(cooldownLeft / 1000))} ثانیه تا ثبت بعدی`
              : willCancel
                ? 'ثبت و لغو کامل سفارش'
                : 'ثبت تغییرات در اسنپ‌پی'}
        </button>
        {/* ⚠️ متن حتماً داخل <span>: در CSS نودِ متنی «فرزند» حساب نمی‌شود، پس بدون این
            span آیکن `svg:only-child` می‌شد و دکمه به قالبِ مربعِ فقط-آیکنی می‌افتاد. */}
        {dirty && (
          <button className="action-btn" disabled={busy} onClick={() => setDraft({})}>
            <RestartAltIcon />
            <span>بازگشت به حالت اول</span>
          </button>
        )}
        <button className="action-btn delete" disabled={busy || cooling} onClick={onCancelOrder}>
          حذف کامل سفارش (لغو در اسنپ‌پی)
        </button>
        <span style={{ fontSize: 11.5, color: cooling ? 'var(--color-warning-dark)' : 'var(--color-text-secondary)', lineHeight: 1.8, fontWeight: cooling ? 600 : 400 }}>
          {cooling
            ? `اسنپ‌پی هنوز در فاصلهٔ اجباری است؛ ${faNum(Math.ceil(cooldownLeft / 1000))} ثانیهٔ دیگر می‌توانید ثبت کنید. (برای جلوگیری از این انتظار، کاهش تعداد و حذف را با هم در یک ثبت انجام دهید.)`
            : 'چند ردیف را با هم تغییر دهید و یک بار ثبت کنید؛ اسنپ‌پی بین هر دو ثبت ۳۰ ثانیه فاصله می‌خواهد.'}
        </span>
      </div>
    </div>
  );
};

export default SnappPayOrderEditor;
